import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { fetchOrder, fetchSettings } from "@/lib/api";
import { fmtCAD } from "@/lib/currency";

export default function InvoicePrint() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [o, s] = await Promise.all([fetchOrder(id), fetchSettings()]);
        setOrder(o);
        setSettings(s);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load invoice");
      }
    })();
  }, [id]);

  // Set document title to a useful download filename when user "Saves as PDF".
  useEffect(() => {
    if (order) {
      const ref = order.order_number || order.id?.slice(0, 8).toUpperCase();
      document.title = `Invoice ${ref} — ${order.business_name || ""}`.trim();
    }
  }, [order]);

  if (err) return <div className="p-8 text-destructive text-sm">{err}</div>;
  if (!order) return <div className="p-8 text-sm text-muted-foreground">Loading invoice…</div>;

  const orderRef = order.order_number || `#${(order.id || "").slice(0, 8).toUpperCase()}`;
  const issuedDate = (order.invoiced_at || order.created_at || "").slice(0, 10);
  const isSK = (order.province || "").toUpperCase() === "SK";

  return (
    <div className="min-h-screen bg-white text-foreground print:bg-white">
      {/* Toolbar — hidden when printing */}
      <div className="no-print sticky top-0 z-10 bg-secondary/80 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Printable invoice · {orderRef}</p>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="invoice-print-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-primary text-primary-foreground text-xs uppercase tracking-wider hover:opacity-90"
          >
            <Printer size={13} strokeWidth={1.75} /> Print / Save PDF
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-10 print:px-12 print:py-8">
        {/* Header */}
        <header className="flex items-start justify-between gap-6 pb-6 border-b border-border">
          <div>
            <h1 className="text-3xl font-serif text-primary tracking-tight">{settings?.brand_name || "Rowen Kids Club"}</h1>
            <p className="text-sm text-muted-foreground mt-1">Wholesale Showroom</p>
            <p className="text-xs text-muted-foreground mt-2">
              Hello@rowenkidsclub.com
              {settings?.gst_number && <> · GST #{settings.gst_number}</>}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Invoice</p>
            <p className="text-2xl font-semibold tabular-nums">{orderRef}</p>
          </div>
        </header>

        {/* Bill to + meta */}
        <section className="grid grid-cols-2 gap-6 py-6 border-b border-border">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Bill to</p>
            <p className="font-medium">{order.business_name || "—"}</p>
            {order.contact_name && <p className="text-sm">{order.contact_name}</p>}
            {order.email && <p className="text-sm">{order.email}</p>}
            {order.shipping_address && (
              <p className="text-sm whitespace-pre-wrap mt-1.5">{order.shipping_address}</p>
            )}
          </div>
          <div className="text-right text-sm space-y-1">
            <MetaRow label="Issued" value={issuedDate || "—"} />
            <MetaRow label="Due" value={order.due_date || "—"} />
            <MetaRow label="Terms" value={order.payment_terms || "—"} />
            <MetaRow label="Province" value={order.province || "—"} />
            <MetaRow label="Status" value={(order.status || "—").toUpperCase()} />
          </div>
        </section>

        {/* Items table */}
        <section className="py-6 border-b border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                <th className="pb-2 pr-3 w-[45%]">Item</th>
                <th className="pb-2 px-2">SKU</th>
                <th className="pb-2 px-2">Size</th>
                <th className="pb-2 px-2 text-right">Qty</th>
                <th className="pb-2 px-2 text-right">Unit</th>
                <th className="pb-2 pl-2 text-right">Line total</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((li, idx) => (
                <tr key={idx} className="border-t border-border">
                  <td className="py-2 pr-3">{li.name}</td>
                  <td className="py-2 px-2 font-mono text-xs">{li.sku || "—"}</td>
                  <td className="py-2 px-2">{li.size || "—"}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{li.quantity}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtCAD(li.unit_wholesale_price)}</td>
                  <td className="py-2 pl-2 text-right tabular-nums">{fmtCAD(li.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Totals */}
        <section className="flex justify-end py-6 border-b border-border">
          <div className="w-72 space-y-1 text-sm">
            <Row label="Units" value={String((order.items || []).reduce((s, li) => s + Number(li.quantity || 0), 0))} />
            <Row label="Subtotal" value={fmtCAD(order.subtotal)} />
            {Number(order.discount_amount) > 0 && (
              <Row label={`Discount (${Number(order.discount_pct) || 0}%)`} value={`− ${fmtCAD(order.discount_amount)}`} />
            )}
            {Number(order.shipping_cost) > 0 && <Row label="Shipping" value={fmtCAD(order.shipping_cost)} />}
            <Row label="GST (5%)" value={fmtCAD(order.gst || 0)} />
            {isSK && Number(order.pst) > 0 && <Row label="PST (6%)" value={fmtCAD(order.pst)} />}
            <div className="flex justify-between font-serif text-lg pt-2 mt-2 border-t border-border">
              <span>Total (CAD)</span>
              <span className="tabular-nums text-primary">{fmtCAD(order.total || 0)}</span>
            </div>
          </div>
        </section>

        {/* Notes + payment */}
        {order.notes && (
          <section className="py-4 border-b border-border">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Order notes</p>
            <p className="text-sm whitespace-pre-wrap">{order.notes}</p>
          </section>
        )}

        {settings?.e_transfer_instructions && (
          <section className="py-6 border-b border-border">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Payment instructions</p>
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{settings.e_transfer_instructions}</p>
          </section>
        )}

        {settings?.invoice_footer_note && (
          <footer className="pt-6 text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">
            {settings.invoice_footer_note}
          </footer>
        )}
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          @page { margin: 16mm; }
        }
      `}</style>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex justify-end gap-3">
      <span className="text-muted-foreground uppercase tracking-[0.12em] text-[10px] self-center">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
